import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { APP_VERSION, SUPPORT_EMAIL } from "@/lib/constants";

interface ErrorReportViewProps {
  error: Error;
  route: string;
  stackTrace: string;
  isSubmitted: boolean;
  onSubmit: () => void;
  onRetry: () => void;
  onBack: () => void;
  onViewDetails: () => void;
}

export function ErrorReportView({
  error,
  route,
  stackTrace,
  isSubmitted,
  onSubmit,
  onRetry,
  onBack,
  onViewDetails,
}: ErrorReportViewProps) {
  const [comment, setComment] = useState("");
  const [ticketId] = useState(() => String(Math.floor(Math.random() * 9000 + 1000)));
  const maxChars = 500;

  // Keyboard shortcuts for submitted state (1-3)
  useEffect(() => {
    if (!isSubmitted) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      switch (e.key) {
        case "1": e.preventDefault(); onRetry(); break;
        case "2": e.preventDefault(); onBack(); break;
        case "3": e.preventDefault(); onViewDetails(); break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); };
  }, [isSubmitted, onRetry, onBack, onViewDetails]);

  const handleSubmit = () => {
    if (!comment.trim()) return;

    const subject = encodeURIComponent(
      `[SpecTree Error Report] ${error.name} — ${route}`
    );
    const body = encodeURIComponent(
      [
        `Error Report`,
        ``,
        `--- Description ---`,
        comment.trim(),
        ``,
        `--- Error ---`,
        `Name: ${error.name}`,
        `Message: ${error.message}`,
        `Route: ${route}`,
        `Timestamp: ${new Date().toISOString()}`,
        `User Agent: ${navigator.userAgent}`,
        ``,
        `--- Stack Trace ---`,
        stackTrace || "N/A",
      ].join("\n")
    );

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      onBack();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      <div className="w-full max-w-[700px] px-4 md:px-0">
        <div className="rounded-xl border border-[#3a3a3a] bg-[#1c1c1c] shadow-[0_16px_60px_rgba(0,0,0,0.7),0_0_120px_10px_rgba(51,230,102,0.06)] overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center h-12 px-5 bg-[#252525]">
            <div className="flex gap-2">
              <div className="w-[13px] h-[13px] rounded-full bg-[#ff5f57]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#febc2e]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-[13px] text-[#6b6b6b] font-mono">
                spectree — report
              </span>
            </div>
            <div className="w-[54px]" />
          </div>

          <div className="h-px bg-[#333]" />

          {/* Terminal body */}
          <div className="bg-[#111] p-4 md:p-7 font-mono">
            {/* Command line */}
            <div className="flex items-center">
              <span className="text-[14px] font-bold text-green-400">$</span>
              <span className="text-[14px] font-medium text-[#e6e6e6]"> spectree report</span>
              <span className="text-[14px] text-[#9ca3b0]"> --issue</span>
            </div>

            <div className="h-5" />

            {isSubmitted ? (
              /* Submitted state */
              <div>
                <div className="flex items-center">
                  <span className="text-[18px] font-bold text-green-400">✓</span>
                  <span className="text-[14px] font-bold text-green-400 ml-1"> Issue submitted successfully</span>
                </div>

                <div className="h-4" />

                {/* Ticket info card */}
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 space-y-1 text-[13px]">
                  <div className="flex">
                    <span className="text-[#9ca3b0] w-24">Ticket:</span>
                    <span className="font-bold text-cyan-400">#ST-{ticketId}</span>
                  </div>
                  <div className="flex">
                    <span className="text-[#9ca3b0] w-24">Status:</span>
                    <span className="text-amber-400">Open — Awaiting triage</span>
                  </div>
                  <div className="flex">
                    <span className="text-[#9ca3b0] w-24">Summary:</span>
                    <span className="text-[#e6e6e6]">{error.message.slice(0, 50)}</span>
                  </div>
                </div>

                <div className="h-4" />
                <p className="text-[12px] text-[#6b6b6b]">
                  Thank you for reporting. Our team has been notified and will follow up.
                </p>

                {/* Divider */}
                <div className="h-6" />
                <div className="h-px bg-[#2b2b2b]" />
                <div className="h-6" />

                <p className="text-[14px] font-medium text-[#e6e6e6] mb-4">
                  What would you like to do?
                </p>

                <div className="space-y-1">
                  <ReportOption number="1" color="text-green-400" icon="↻" label="Retry connection" onClick={onRetry} />
                  <ReportOption number="2" color="text-[#deb94a]" icon="←" label="Go back to dashboard" onClick={onBack} />
                  <ReportOption number="3" color="text-cyan-400" icon="⌥" label="View error details" onClick={onViewDetails} />
                </div>

                <div className="h-7" />
                <div className="flex items-center text-[13px]">
                  <span className="text-[#9ca3b0]">Press a number to continue </span>
                  <span className="text-green-400 animate-terminal-blink">▊</span>
                </div>
              </div>
            ) : (
              /* Typing state */
              <div>
                <div className="flex items-center">
                  <span className="text-[14px]">📝</span>
                  <span className="text-[14px] font-bold text-[#e6e6e6] ml-1"> Report an Issue</span>
                </div>
                <p className="text-[12px] text-[#6b6b6b] mt-1">
                  Describe what went wrong. Be as specific as possible.
                </p>

                {/* Divider */}
                <div className="h-5" />
                <div className="h-px bg-[#2b2b2b]" />
                <div className="h-5" />

                {/* Text area */}
                <textarea
                  value={comment}
                  onChange={(e) => {
                    if (e.target.value.length <= maxChars) {
                      setComment(e.target.value);
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="What were you doing when this happened?"
                  rows={6}
                  autoFocus
                  className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-[13px] font-mono text-[#d9d9d9] placeholder:text-[#555] focus:outline-none focus:border-[#444] focus:ring-1 focus:ring-[#444] resize-none"
                />

                {/* Character counter */}
                <div className="mt-2 text-xs">
                  <span className="text-[#9ca3b0]">{String(comment.length)}</span>
                  <span className="text-[#6b6b6b]">/{String(maxChars)} characters</span>
                </div>

                {/* Divider */}
                <div className="h-5" />
                <div className="h-px bg-[#2b2b2b]" />
                <div className="h-5" />

                {/* Submit instructions */}
                <div className="flex items-center text-[13px]">
                  <span className="text-[#9ca3b0]">Press&nbsp;</span>
                  <span className="font-bold text-green-400">[Enter]</span>
                  <span className="text-[#9ca3b0]">&nbsp;to submit, or&nbsp;</span>
                  <span className="font-bold text-amber-400">[Esc]</span>
                  <span className="text-[#9ca3b0]">&nbsp;to cancel</span>
                </div>

                {/* Prompt */}
                <div className="mt-3 flex items-center">
                  <span className="text-[14px] font-bold text-green-400">&gt;</span>
                  <span className="text-green-400 ml-1 animate-terminal-blink">▊</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 text-[12px] text-[#4b5563] font-sans">
        spectree {APP_VERSION}
      </div>
    </div>
  );
}

function ReportOption({
  number,
  color,
  icon,
  label,
  onClick,
}: {
  number: string;
  color: string;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 rounded-md px-3 py-2 hover:bg-white/5 transition-colors w-full text-left cursor-pointer"
    >
      <span className="flex items-center font-mono text-[14px]">
        <span className="text-[#555]">[</span>
        <span className={cn("font-bold", color)}>{number}</span>
        <span className="text-[#555]">]</span>
      </span>
      <span className="flex items-center gap-1 font-mono text-[14px]">
        <span className="text-[#888]">{icon}  </span>
        <span className="text-[#e5e5e5]">{label}</span>
      </span>
    </button>
  );
}
